import React, { useState } from "react";
import EGFileUpload from "../../../../../MainComponents/FileUpload/EGFileUpload/EGFileUpload";
import EGDatePicker from "../../../../../MainComponents/DatePicker/EGDatePicker";
import { Box, Paper, Stack, Typography, useTheme } from "@mui/material";
import { IconHeader } from "../../../../../Icon/IconHader/IconHeader";
import { EGBasicSelect } from "../../../../../MainComponents/Select/EGBasicSelect";
import { EGInput } from "../../../../../MainComponents/Input/EGInput";
import { BoldAttachCircle, CalendarCircleBold } from "../../../../../Icon";
import { EGIconButton } from "../../../../../MainComponents/Button/IconButton/IconButton";
import { useReportContext } from "../../../../ReportContext/useReportContext";
import { EGButton } from "../../../../../MainComponents/Button/EGButton/EGButton";
import { useTranslation } from "react-i18next";
import { createAttachment, updateResolutions } from "../../../../../../apis";
import { useSnackbar } from "notistack";
import { parseDateToDDMMYYYY2 } from "../../../../../../helpers/dateTransform";
import { ModalAttachments } from "../../../../../Modal/Attachments/ModalAttachments";
import Icon from "../../../../../Icon/Icon";
import dayjs from "dayjs";
import { useReportId } from "../../../../../../context/report/ReportContext";

export const Resolution = ({ reportDetail, reportRender }) => {

  // ----->  Custom Hooks <----- //
  const [t] = useTranslation("report");
  const { facts, evaluated_facts } = useReportContext();
  const { iconName, color, label } =
    evaluated_facts[reportDetail?.resolution?.facts_verified] || "no";
  const theme = useTheme();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const id = useReportId();

  // -----> Initial States <----- //
  // Is editing?
  const [resolutioEditing, setResolutionEditing] = useState(() => {
    const verificated = reportDetail?.resolution?.facts_verified;
    return verificated ? true : false;
  });

  // Resolution dates
  const [date, setDate] = useState(() => {
    const resolutionDate = reportDetail?.resolution?.date;
    return resolutionDate ? dayjs(resolutionDate) : dayjs();
  });
  // Facts verifications
  const [facts_verified, setFacts_verified] = useState(
    reportDetail?.resolution?.facts_verified || ""
  );
  // General comments
  const [comment, setComment] = useState(
    reportDetail?.resolution?.comment || ""
  );
  // File comments
  const [fileComments, setFileComments] = useState({});
  // Files report attachment
  const [filesResolution, setFilesResolution] = useState([]);
  // Component loading
  const [isLoading, setIsLoading] = useState(false);
  // Comments onChange
  const handleCommentChange = (fileID, value) => {
    setFileComments((prevComments) => ({
      ...prevComments,
      [fileID]: value,
    }));
  };
  // Open modal attachments
  const [openModal, setOpenModal] = useState(false);

  const hdlOpenModal = () => setOpenModal(true);
  const hdlCloseModal = () => setOpenModal(false);

  // Delete selected file
  const removeFile = (fileID) => {
    setFilesResolution((prevFiles) =>
      prevFiles.filter((file) => file.file.name !== fileID)
    );
    setFileComments((prevComments) => {
      const newComments = { ...prevComments };
      delete newComments[fileID];
      return newComments;
    });
  };

  // Short title file
  const fileTitle = (fileName) => {
    if (fileName?.length > 30) {
      const firstPt = fileName.substring(0, 18);
      const lastPt = fileName.substring(fileName.length - 8);
      return `${firstPt} ... ${lastPt}`;
    }
    return fileName;
  };

  const sendingResolutionData = async (e) => {
    e.preventDefault();
    let files_id;
    let filesToSend = [];
    setIsLoading(true);

    const sending = enqueueSnackbar(`${t("Resolution.sending")}...`, {
      variant: "info",
      persist: true,
    });

    try {
      if (filesResolution.length >= 1) {
        const infoObj = {};
        filesResolution.forEach((file, index) => {
          infoObj[file.file.name] = fileComments[file.file.name];
        });

        for (const fileObj of filesResolution) {
          const { formData } = fileObj;
          const filesRes = await createAttachment(formData, fileComments);

          files_id = filesRes.attachments_id;
          filesToSend.push({
            file: files_id,
            name: fileObj.file.name,
            info: fileComments[fileObj.file.name],
            size: fileObj.file.size,
          });
        }
      }

      const resolution = {
        comment,
        date,
        files: filesToSend,
        facts_verified,
      };

      const resp = await updateResolutions(id, resolution).then((resp) => {
        if (resp.status === 200) {
          enqueueSnackbar(t("Resolution.data_successfully_updated"), {
            variant: "success",
          });
          setIsLoading(false);
          reportRender();
          setFilesResolution([]);
          // Clean form
          closeSnackbar(sending);
          // setEditResolution(false);
        }
      });
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      closeSnackbar(sending);
    }
  };

  return (
    <Box my={1.5}>
      <IconHeader title={t("Resolution.general_details_resolution_report")} />
      {!resolutioEditing ? (
        <Box component="form">
          <Stack
            spacing={2}
            display="flex"
            alignItems="start"
            justifyContent="space-between"
            flexDirection="row"
            flexWrap={{ xs: "wrap", md: "nowrap" }}
            my={2}
            height={"auto"}
          >
            <EGDatePicker
              label={`${t("Resolution.resolution_date")}:`}
              value={date}
              onChange={(resolDate) => setDate(resolDate)}
            />
            <EGBasicSelect
              label={t("Resolution.have_facts_been_verified")}
              options={facts}
              onChange={(e) => setFacts_verified(e.target.value)}
              value={facts_verified}
              required
            />
          </Stack>

          <EGInput
            multiline
            rows={3}
            label={`${t("Resolution.general_comments_on_resolution")}:`}
            placeholder={t(
              "Resolution.describes_detail_general_comments_resolution_report"
            )}
            fullWidth
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />

          <Stack mt={2}>
            <EGFileUpload
              title={t("Resolution.resolution_support_files")}
              files={filesResolution}
              setFiles={setFilesResolution}
              disabled={isLoading}
            />
            {filesResolution.length >= 1 && (
              <Box
                className="rounded"
                border={`1px solid ${theme.palette.teal.main}`}
                mt={2}
                px={1}
                py={0.5}
                boxShadow={`0 0 4px -1px ${theme.palette.teal.main}`}
              >
                {filesResolution.map((file, i) => (
                  <Stack
                    key={file.file.lastModified}
                    display="flex"
                    flexDirection="column"
                    className={
                      i < filesResolution.length - 1 ? "border-bottom" : ""
                    }
                  >
                    <Stack
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      flexDirection="row"
                    >
                      <Typography
                        color="secondary"
                        variant="body2"
                        component="span"
                      >
                        <BoldAttachCircle
                          color={theme.palette.teal.main}
                          className="mr-10"
                        />
                        {`${fileTitle(file.file.name)} (${Math.round(
                          file.file.size / 1024
                        )} KB)`}
                      </Typography>

                      <EGIconButton
                        iconName="trash_can"
                        iconSize={17}
                        iconColor="error"
                        onClick={() => removeFile(file.file.name)}
                      />
                    </Stack>
                    <Paper elevation={0} sx={{ px: 3.5 }}>
                      <EGInput
                        value={fileComments[file.file.name || ""]}
                        onChange={(e) =>
                          handleCommentChange(file.file.name, e.target.value)
                        }
                        label={`${t("Resolution.file_comments")}:`}
                        sx={{ mt: 0, mb: 1.5 }}
                        multiline
                        rows={2}
                      />
                    </Paper>
                  </Stack>
                ))}
              </Box>
            )}
          </Stack>

          <Stack
            display="flex"
            alignItems="center"
            justifyContent="end"
            flexDirection="row"
            spacing={3}
            mt={2}
          >
            {reportDetail?.resolution?.facts_verified &&
              !resolutioEditing &&
              !isLoading && (
                <EGButton
                  iconName="close_icon"
                  iconSize={21}
                  label={t("Resolution.cancel")}
                  onClick={() => setResolutionEditing(true)}
                />
              )}
            <EGButton
              label={t("Resolution.save")}
              onClick={sendingResolutionData}
              loading={isLoading}
              iconName={
                facts_verified === "" ? "mark_check" : "mark_check_bold"
              }
              disabled={facts_verified === ""}
              color={facts_verified === "" ? "light" : "success"}
              iconColor={facts_verified === "" ? "" : "white"}
              iconColorHover={facts_verified === "" ? "" : "white"}
              labelColor={facts_verified === "" ? "" : "white"}
            />
          </Stack>
        </Box>
      ) : (
        <>
          {reportDetail?.resolution?.files && reportDetail.resolution.files.length > 0 && (
            <ModalAttachments
              open={openModal}
              onClose={hdlCloseModal}
              files={reportDetail.resolution.files}
            />
          )}
          <Box sx={{ position: "relative" }} className="border-bottom">
            <Paper
              elevation={0}
              sx={{ position: "absolute", top: -5, right: 0 }}
            >
              <EGIconButton
                iconSize={20}
                iconColor="primary"
                iconName="outline_edit"
                onClick={() => setResolutionEditing(false)}
                title={t("Resolution.edit")}
              />
            </Paper>
            <Box display="flex" alignItems="center" gap={1} my={1}>
              <CalendarCircleBold color={theme.palette.primary.main} />
              <Typography color="secondary" variant="subtitle1">
                {t("Resolution.resolution_date")}:
              </Typography>
              <Typography color="primary" variant="h6">
                {parseDateToDDMMYYYY2(reportDetail.resolution.date)}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1} my={1}>
              <Icon name={iconName} color={theme.palette[color].main} />
              <Typography color="secondary" variant="subtitle1">
                {label}
              </Typography>
            </Box>

            {reportDetail?.resolution?.comment && (
              <Box my={1}>
                <Typography variant="subtitle2" color="primary">
                  {t("Resolution.general_comments_on_resolution")}
                </Typography>
                <Typography>{reportDetail?.resolution?.comment}</Typography>
              </Box>
            )}
          </Box>
          <Box mt={2}>
            <EGButton
              iconName="attach_circle"
              label={
                reportDetail?.resolution?.files.length === 0
                  ? t("Resolution.no_evidence")
                  : t("Resolution.evidence")
              }
              disabled={reportDetail?.resolution?.files.length === 0}
              onClick={hdlOpenModal}
            />
          </Box>
        </>
      )}
    </Box>
  );
};
